import React, { useMemo, useState } from "react";
import PanamaLocationPickerModal, { buildCoverageLabel } from "./PanamaLocationPickerModal";

function normalizeArea(area, idx = 0) {
  if (!area) return null;

  if (typeof area === "string") {
    const label = area.trim();
    if (!label) return null;
    return {
      id: `legacy-${idx}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      country: "PA",
      level: "zone",
      label,
      extraFee: 0,
      legacy: true,
    };
  }

  const level = area.level || (area.zone ? "zone" : area.corregimiento ? "corregimiento" : area.district ? "district" : area.province ? "province" : "zone");
  const label = area.label || buildCoverageLabel({ ...area, level });
  if (!label) return null;

  return {
    id: area.id || `${level}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    country: area.country || area.countryCode || "PA",
    province: area.province || null,
    district: area.district || null,
    corregimiento: area.corregimiento || null,
    zone: area.zone || null,
    level,
    label,
    extraFee: Number(area.extraFee || area.fee || area.deliveryFee || 0) || 0,
  };
}

export function normalizeServiceAreasAdvanced(value) {
  return (Array.isArray(value) ? value : [])
    .map(normalizeArea)
    .filter(Boolean);
}


export function serviceAreasToLabels(value) {
  return normalizeServiceAreasAdvanced(value)
    .map((area) => area.label)
    .filter(Boolean);
}

export default function PanamaServiceAreasPicker({
  value = [],
  onChange,
  title = "Zonas donde atiendes",
  subtitle = "Agrega provincias, distritos, corregimientos o zonas específicas. Puedes asignar un costo adicional fijo por zona.",
}) {
  const areas = useMemo(() => normalizeServiceAreasAdvanced(value), [value]);
  const [open, setOpen] = useState(false);

  const setAreas = (next) => {
    onChange && onChange(normalizeServiceAreasAdvanced(next));
  };

  const addArea = (loc) => {
    const normalized = normalizeArea(loc, areas.length);
    if (!normalized) return;

    const key = [
      normalized.level,
      normalized.province,
      normalized.district,
      normalized.corregimiento,
      normalized.zone,
    ].filter(Boolean).join("|").toLowerCase();

    const exists = areas.some((area) => {
      const areaKey = [
        area.level,
        area.province,
        area.district,
        area.corregimiento,
        area.zone,
      ].filter(Boolean).join("|").toLowerCase();
      return areaKey === key;
    });

    const next = exists
      ? areas.map((area) => {
          const areaKey = [
            area.level,
            area.province,
            area.district,
            area.corregimiento,
            area.zone,
          ].filter(Boolean).join("|").toLowerCase();
          return areaKey === key ? { ...area, extraFee: normalized.extraFee } : area;
        })
      : [...areas, normalized];

    setAreas(next);
    setOpen(false);
  };

  const removeArea = (idx) => {
    setAreas(areas.filter((_, i) => i !== idx));
  };

  const updateFee = (idx, fee) => {
    setAreas(areas.map((area, i) => i === idx ? { ...area, extraFee: Number(fee || 0) || 0 } : area));
  };

  return (
    <div className="pa-service-areas">
      <style>{styles}</style>

      <div className="pa-service-areas-head">
        <div>
          <strong>{title}</strong>
          <span>{subtitle}</span>
        </div>
        <button type="button" onClick={() => setOpen(true)}>Agregar zona</button>
      </div>

      {areas.length === 0 ? (
        <div className="pa-service-areas-empty">
          Todavía no has agregado zonas. Puedes agregar cobertura amplia como “Panamá” o específica como “Costa del Este”.
        </div>
      ) : (
        <div className="pa-service-areas-list">
          {areas.map((area, idx) => (
            <div className="pa-service-area-row" key={area.id || `${area.label}-${idx}`}>
              <div>
                <strong>{area.label}</strong>
                <span>{area.level === "province" ? "Provincia completa" : area.level === "district" ? "Distrito" : area.level === "corregimiento" ? "Corregimiento" : "Zona específica"}</span>
              </div>

              <label>
                <em>+$</em>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={area.extraFee || 0}
                  onChange={(e) => updateFee(idx, e.target.value)}
                />
              </label>

              <button type="button" className="remove" onClick={() => removeArea(idx)}>✕</button>
            </div>
          ))}
        </div>
      )}

      <PanamaLocationPickerModal
        open={open}
        onClose={() => setOpen(false)}
        title="Agregar zona de cobertura"
        subtitle="Puedes guardar desde provincia completa hasta zona exacta. El costo adicional es fijo."
        requireAllLevels={false}
        allowPartialSelection
        coverageMode
        showExtraFee
        confirmLabel="Agregar"
        onConfirm={addArea}
      />
    </div>
  );
}

const styles = `
.pa-service-areas{border:1px solid #e2e8f0;background:#fff;border-radius:24px;padding:14px;display:grid;gap:12px}
.pa-service-areas-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px}
.pa-service-areas-head strong{display:block;color:#0f172a;font-size:15px;font-weight:950}
.pa-service-areas-head span{display:block;color:#64748b;font-size:12px;line-height:1.4;font-weight:750;margin-top:4px}
.pa-service-areas-head button{border:0;background:#00675f;color:#fff;border-radius:999px;min-height:38px;padding:0 13px;font-size:12px;font-weight:950;cursor:pointer;white-space:nowrap}
.pa-service-areas-empty{border:1px dashed #cbd5e1;background:#f8fafc;border-radius:18px;padding:14px;color:#64748b;font-size:12px;line-height:1.45;font-weight:750}
.pa-service-areas-list{display:grid;gap:9px}
.pa-service-area-row{display:grid;grid-template-columns:minmax(0,1fr) 118px 36px;gap:9px;align-items:center;border:1px solid #edf2f7;background:#f8fafc;border-radius:18px;padding:10px}
.pa-service-area-row strong{display:block;color:#0f172a;font-size:13px;font-weight:950;line-height:1.25}
.pa-service-area-row span{display:block;color:#64748b;font-size:11px;font-weight:800;margin-top:3px}
.pa-service-area-row label{height:38px;border:1px solid #e2e8f0;background:#fff;border-radius:14px;display:flex;align-items:center;gap:3px;padding:0 9px;box-sizing:border-box}
.pa-service-area-row label em{font-style:normal;color:#00675f;font-size:12px;font-weight:950}
.pa-service-area-row input{width:100%;border:0;background:transparent;outline:none;color:#0f172a;font-weight:950}
.pa-service-area-row .remove{height:36px;width:36px;border:0;border-radius:999px;background:#fef2f2;color:#dc2626;font-weight:950;cursor:pointer}
@media(max-width:640px){.pa-service-areas-head{display:grid}.pa-service-areas-head button{width:100%}.pa-service-area-row{grid-template-columns:1fr}.pa-service-area-row .remove{width:100%}}
`;
